import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  selectedContentTypes: number[];
  onToggleContentType: (id: number) => void;
  contentTypes: { id: number; name: string }[];
  typesLoading: boolean;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
}

export default function SpecializationStep({
  selectedContentTypes,
  onToggleContentType,
  contentTypes,
  typesLoading,
  onBack,
  onSubmit,
  loading,
}: Props) {
  const { t } = useLanguage();
  return (
    <motion.form initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} onSubmit={onSubmit} className="space-y-5">
      <div className="text-center mb-2">
        <h3 className="text-xl font-bold text-gray-800 mb-1">{t("auth.specializationStep.title")}</h3>
        <p className="text-sm text-gray-500">{t("auth.specializationStep.subtitle")}</p>
      </div>

      {/* Content Creator Types Section */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          <Sparkles className="inline me-2" size={16} />
          {t("auth.specializationStep.contentCreatorTypes")} <span className="text-red-500">*</span>
        </label>
        {typesLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">{t("auth.specializationStep.loadingTypes")}</p>
          </div>
        ) : contentTypes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">{t("auth.specializationStep.noTypesAvailable")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {contentTypes.map((type) => {
              const isSelected = selectedContentTypes.includes(type.id);
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => onToggleContentType(type.id)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 text-start transition-all duration-200 ${
                    isSelected
                      ? "border-[#b3d130] bg-[#f9fce8]"
                      : "border-gray-200 hover:border-[#b3d130]/50"
                  }`}
                >
                  <span
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      isSelected
                        ? "border-[#b3d130] bg-[#b3d130]"
                        : "border-gray-300"
                    }`}
                  >
                    {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                  </span>
                  <span className="text-sm font-semibold text-gray-700">{type.name}</span>
                </button>
              );
            })}
          </div>
        )}
        {selectedContentTypes.length === 0 && (
          <p className="text-xs text-red-500 text-center mt-2">{t("auth.validation.selectContentCreatorType")}</p>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 rounded-xl border border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          {t("common.back")}
        </button>
        <button
          type="submit"
          disabled={loading  || selectedContentTypes.length === 0}
          className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#b3d130] to-[#a5c428] text-gray-900 font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t("auth.specializationStep.creating") : t("auth.specializationStep.createAccount")}
        </button>
      </div>
    </motion.form>
  );
}
